import { useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { countWords } from "@/lib/utils";
import type { Json } from "@/integrations/supabase/types";

export interface ScriptSettings {
  fontSize: number;
  fontColor: string;
  bgColor: string;
  scrollSpeed: number;
  mirrorMode: boolean;
  lineHeight?: number;
  lineMargin?: number;
  horizontalPadding?: number;
}

export interface Script {
  id: string;
  user_id: string;
  title: string;
  content: string;
  scene_type: string;
  settings: ScriptSettings;
  word_count: number;
  created_at: string;
  updated_at: string;
}

const defaultSettings: ScriptSettings = {
  fontSize: 48,
  fontColor: "#ffffff",
  bgColor: "#1a1a1a",
  scrollSpeed: 50,
  mirrorMode: false,
  lineHeight: 1.5,
  horizontalPadding: 10,
};

const SCRIPTS_CACHE_KEY = 'vesper_scripts_cache';

interface CachedScripts {
  scripts: Script[];
  userId: string;
  timestamp: number;
}

// Helper query function
const fetchScriptsData = async (userId: string) => {
  const { data, error } = await supabase
    .from("scripts")
    .select("*")
    .eq("user_id", userId)
    .order("updated_at", { ascending: false });

  if (error) throw error;

  return (data || []).map((script) => ({
    ...script,
    settings: typeof script.settings === 'string'
      ? JSON.parse(script.settings)
      : script.settings || defaultSettings,
  })) as Script[];
};

export function useScripts() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Basic query for scripts
  const { 
    data: scripts = [], 
    isLoading: loading, 
    error,
    refetch 
  } = useQuery({
    queryKey: ["scripts", user?.id],
    queryFn: () => user ? fetchScriptsData(user.id) : Promise.resolve([]),
    enabled: !!user,
    staleTime: 1000 * 60, // 1 minute stale time
  });

  // Update local cache whenever scripts change
  if (user && scripts.length > 0) {
    try {
       localStorage.setItem(SCRIPTS_CACHE_KEY, JSON.stringify({
         scripts,
         userId: user.id,
         timestamp: Date.now(),
       }));
    } catch {
      // Ignore cache write failures (e.g., quota or privacy mode).
    }
  }

  // Mutations
  const createMutation = useMutation({
    mutationFn: async (sceneType: string) => {
      if (!user) throw new Error("No user");
      const { data, error } = await supabase
        .from("scripts")
        .insert([{
          user_id: user.id,
          scene_type: sceneType,
          title: "Untitled Script",
          content: "",
          settings: defaultSettings as unknown as Json,
          word_count: 0,
        }])
        .select()
        .single();

      if (error) throw error;
      return {
        ...data,
        settings: (data.settings as unknown as ScriptSettings) || defaultSettings,
      } as Script;
    },
    onSuccess: (newScript) => {
      queryClient.setQueryData(["scripts", user?.id], (old: Script[] = []) => [newScript, ...old]);
    },
    onError: (err) => {
      toast({
        title: "Error",
        description: "Failed to create script",
        variant: "destructive",
      });
      console.error("Error creating script:", err);
    }
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<Script> }) => {
      if (!user) throw new Error("No user");
      const updateData: Record<string, unknown> = { ...updates };
      if (updates.content !== undefined) {
        updateData.word_count = countWords(updates.content);
      }

      const { error } = await supabase
        .from("scripts")
        .update(updateData)
        .eq("id", id)
        .eq("user_id", user.id);

      if (error) throw error;
      return { id, updateData };
    },
    onSuccess: ({ id, updateData }) => {
      queryClient.setQueryData(["scripts", user?.id], (old: Script[] = []) =>
        old.map((script) =>
          script.id === id ? { ...script, ...updateData } as Script : script
        )
      );
    },
    onError: (err) => {
      console.error("Error updating script:", err);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      if (!user) throw new Error("No user");
      const { error } = await supabase
        .from("scripts")
        .delete()
        .eq("id", id)
        .eq("user_id", user.id);

      if (error) throw error;
      return id;
    },
    onSuccess: (deletedId) => {
      queryClient.setQueryData(["scripts", user?.id], (old: Script[] = []) =>
        old.filter((script) => script.id !== deletedId)
      );
      toast({
        title: "Script deleted",
        description: "Your script has been deleted successfully",
      });
    },
    onError: (err) => {
      toast({
        title: "Error",
        description: "Failed to delete script",
        variant: "destructive",
      });
      console.error("Error deleting script:", err);
    }
  });

  const getScript = useCallback(async (id: string): Promise<Script | null> => {
    if (!user) return null;
    // Check query cache first
    const cachedScript = scripts.find(s => s.id === id);
    if (cachedScript) return cachedScript;

    // Fallback to fetch
    try {
      const { data, error } = await supabase
        .from("scripts")
        .select("*")
        .eq("id", id)
        .eq("user_id", user.id)
        .maybeSingle();

      if (error) throw error;
      if (!data) return null;
      
      return {
        ...data,
        settings: typeof data.settings === 'string' 
          ? JSON.parse(data.settings) 
          : data.settings || defaultSettings,
      } as Script;
    } catch (err) {
      console.error("Error fetching script:", err);
      return null;
    }
  }, [user, scripts]);

  return {
    scripts,
    loading,
    error: error as Error | null,
    createScript: (sceneType?: string) => createMutation.mutateAsync(sceneType || "recording"),
    updateScript: (id: string, updates: Partial<Script>) => updateMutation.mutateAsync({ id, updates }).then(() => true).catch(() => false),
    deleteScript: (id: string) => deleteMutation.mutateAsync(id).then(() => true).catch(() => false),
    getScript,
    refetch,
  };
}
